import { fetchDrivingRoute } from './map-route.js';
import { hasValidCoords } from './map-coords.js';
import { tripCoverUrl } from './trip-covers.js';
import { toDayKey } from './day-key.js';
import { resolveTripBases } from './trip-bases.js';

const TILE_SERVERS = ['a', 'b', 'c'];
const OFFLINE_PACK_KEY = (tripId) => `metis-offline-pack:${tripId}`;

function lon2tile(lon, zoom) {
  return Math.floor(((lon + 180) / 360) * 2 ** zoom);
}

function lat2tile(lat, zoom) {
  const rad = (lat * Math.PI) / 180;
  return Math.floor(((1 - Math.log(Math.tan(rad) + 1 / Math.cos(rad)) / Math.PI) / 2) * 2 ** zoom);
}

function collectCoords(bundle) {
  const coords = [];
  const push = (lat, lng) => {
    if (Number.isFinite(lat) && Number.isFinite(lng)) coords.push({ lat, lng });
  };

  for (const day of bundle.days ?? []) {
    for (const stop of day.stops ?? []) {
      push(stop.lat, stop.lng);
    }
  }

  const live = bundle.live ?? {};
  for (const base of resolveTripBases(live)) {
    push(base.lat, base.lng);
  }
  for (const item of live.wishlist ?? []) {
    push(item.lat, item.lng);
  }

  return coords;
}

function boundsFromCoords(coords, paddingDeg = 0.08) {
  if (!coords.length) {
    return { north: 46.5, south: 45.4, west: 13.2, east: 15.2 };
  }
  let north = -90;
  let south = 90;
  let west = 180;
  let east = -180;
  for (const { lat, lng } of coords) {
    north = Math.max(north, lat);
    south = Math.min(south, lat);
    west = Math.min(west, lng);
    east = Math.max(east, lng);
  }
  return {
    north: north + paddingDeg,
    south: south - paddingDeg,
    west: west - paddingDeg,
    east: east + paddingDeg,
  };
}

function uniqueTiles(tiles) {
  const seen = new Set();
  return tiles.filter((t) => {
    const key = `${t.z}/${t.x}/${t.y}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function tilesForBounds(bounds, zoom) {
  const xMin = lon2tile(bounds.west, zoom);
  const xMax = lon2tile(bounds.east, zoom);
  const yMin = lat2tile(bounds.north, zoom);
  const yMax = lat2tile(bounds.south, zoom);
  const tiles = [];
  for (let x = xMin; x <= xMax; x += 1) {
    for (let y = yMin; y <= yMax; y += 1) {
      tiles.push({ z: zoom, x, y });
    }
  }
  return tiles;
}

function tilesAroundPoint(lat, lng, zoom, radius = 2) {
  const cx = lon2tile(lng, zoom);
  const cy = lat2tile(lat, zoom);
  const tiles = [];
  for (let dx = -radius; dx <= radius; dx += 1) {
    for (let dy = -radius; dy <= radius; dy += 1) {
      tiles.push({ z: zoom, x: cx + dx, y: cy + dy });
    }
  }
  return tiles;
}

function buildTileList(coords, bounds) {
  const tiles = [];
  for (const z of [9, 10, 11]) {
    tiles.push(...tilesForBounds(bounds, z));
  }
  for (const z of [12, 13, 14]) {
    for (const { lat, lng } of coords) {
      tiles.push(...tilesAroundPoint(lat, lng, z, 2));
    }
    if (!coords.length) {
      tiles.push(...tilesForBounds(bounds, z));
    }
  }
  return uniqueTiles(tiles);
}

async function fetchTile({ z, x, y }, serverIndex) {
  const host = TILE_SERVERS[serverIndex % TILE_SERVERS.length];
  const url = `https://${host}.tile.openstreetmap.org/${z}/${x}/${y}.png`;
  try {
    const res = await fetch(url, { mode: 'cors', credentials: 'omit' });
    if (!res.ok) throw new Error('tile');
  } catch {
    /* best effort */
  }
}

async function prefetchTiles(coords, bounds, { onProgress } = {}) {
  const tiles = buildTileList(coords, bounds);
  const total = tiles.length;
  let done = 0;
  const concurrency = 6;
  let cursor = 0;

  async function worker(serverIndex) {
    while (cursor < tiles.length) {
      const index = cursor;
      cursor += 1;
      await fetchTile(tiles[index], serverIndex);
      done += 1;
      onProgress?.({ phase: 'tiles', done, total });
    }
  }

  await Promise.all(
    Array.from({ length: Math.min(concurrency, tiles.length) }, (_, i) => worker(i)),
  );
  return { tiles: total };
}

async function prefetchRoutes(bundle, { onProgress } = {}) {
  const instanceId = bundle.instance?.id ?? bundle.instanceId;
  if (!instanceId) return { routes: 0 };

  const days = bundle.days ?? [];
  let done = 0;
  for (const day of days) {
    const dayKey = toDayKey(day.date);
    const coords = (day.stops ?? [])
      .filter((s) => hasValidCoords({ lat: s.lat, lng: s.lng }))
      .map((s) => ({ lat: s.lat, lng: s.lng }));
    if (coords.length >= 2) {
      await fetchDrivingRoute(coords, { instanceId, dayKey });
    }
    done += 1;
    onProgress?.({ phase: 'routes', done, total: days.length });
  }
  return { routes: days.length };
}

async function prefetchCover(country) {
  const url = tripCoverUrl(country);
  try {
    await fetch(url, { mode: 'cors', credentials: 'omit' });
  } catch {
    /* best effort */
  }
}

const OFFLINE_PACK_TTL_MS = 7 * 86400000;

export function isOfflinePackComplete(tripId) {
  try {
    const raw = localStorage.getItem(OFFLINE_PACK_KEY(tripId));
    if (!raw) return false;
    const { at } = JSON.parse(raw);
    return Number.isFinite(at) && Date.now() - at < OFFLINE_PACK_TTL_MS;
  } catch {
    return false;
  }
}

export function markOfflinePackComplete(tripId) {
  try {
    localStorage.setItem(OFFLINE_PACK_KEY(tripId), JSON.stringify({ at: Date.now() }));
  } catch {
    /* ignore */
  }
}

/**
 * Warm SW + app caches for mountain use: map tiles, driving routes, shelf cover.
 * @param {object} bundle — trip context or cached bundle shape
 * @param {{ force?: boolean, onProgress?: (p: { phase: string, done: number, total: number }) => void }} [opts]
 */
export async function buildOfflinePack(bundle, { force = false, onProgress } = {}) {
  const tripId = bundle.tripId ?? bundle.trip?.id;
  if (!tripId) return { skipped: true };

  if (!force && isOfflinePackComplete(tripId)) {
    return { skipped: true, reason: 'already-complete' };
  }

  const coords = collectCoords(bundle);
  const bounds = boundsFromCoords(coords);

  onProgress?.({ phase: 'cover', done: 0, total: 1 });
  await prefetchCover(bundle.trip?.country);
  onProgress?.({ phase: 'cover', done: 1, total: 1 });

  const tileResult = await prefetchTiles(coords, bounds, { onProgress });
  const routeResult = await prefetchRoutes(bundle, { onProgress });

  markOfflinePackComplete(tripId);
  return {
    skipped: false,
    tiles: tileResult.tiles,
    routes: routeResult.routes,
    coords: coords.length,
  };
}
